import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { categoriaDe, classeAcoDe, corDe, idRaiz } from '../../dominio/didatico/catalogo'
import {
  estadoDe,
  pecaTranslucida,
  pecaVisivel,
  type EstadoPeca,
  type MapaEstados,
} from '../../dominio/didatico/estadoPecas'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { CORES_SEMANTICAS, type CategoriaDidatica } from '../../dominio/didatico/tokens'
import { corDaClasseAco } from '../../dominio/didatico/classesAco'
import type { HitVeiculo } from '../../dominio/didatico/inspecaoVeicular'

export type ModoVisualizacao =
  | 'externo'
  | 'estrutura'
  | 'srs'
  | 'cinematica'
  | 'estabilizacao'
  | 'tabletop'
  | 'extracao'
  | 'vidros'
  | 'interior'
  | 'mecanica'
  | 'energia'

export type Camadas = Record<CategoriaDidatica, boolean>

/**
 * Opacidade de referência de cada camada quando ela está ligada.
 * A carroceria é tratada à parte porque o aluno controla a translucidez dela.
 */
const OPACIDADE_BASE: Record<CategoriaDidatica, number> = {
  estrutura: 1,
  zonaDeformacao: 0.3,
  srs: 0.8,
  retencao: 1,
  carroceria: 1,
  habitaculo: 0.92,
  mecanica: 1,
  energia: 1,
  vitima: 1,
}

export interface ValorMaquete {
  perfil: PerfilVeiculo
  modo: ModoVisualizacao
  camadas: Camadas
  /** 0 = carroceria invisível, 1 = opaca */
  opacidadeCarroceria: number
  estados: MapaEstados
  selecionada: string | null
  selecionar: (id: string | null) => void
  mostrarRotulos: boolean
  /** expansão do airbag no instante atual da cinemática (0..1) */
  expansaoAirbag: number
  /**
   * Quais dispositivos SRS o evento em curso aciona.
   *
   * Vem do tipo de colisão, não do módulo: na traseira nenhum dispara, e na
   * lateral só os do lado do impacto. Apresentar todos expandindo em qualquer
   * evento é exatamente o que §21 do PROMPT2 proíbe.
   */
  srsAcionados: readonly string[]
  /** o cinto/pretensionador estão em evidência neste instante */
  retencaoEmEvidencia: boolean
  /**
   * Peças que obstruem a rota de retirada em análise. Ficam visíveis e em cor
   * de alerta mesmo com a camada de estrutura desligada: o aluno precisa ver
   * o que está no caminho sem ter de procurar.
   */
  idsEmAlerta: readonly string[]
  /** colunas seccionadas pela técnica de teto, para marcar o ponto de corte */
  colunasSeccionadas: readonly string[]
  /**
   * Pinta a estrutura pela classe de aço em vez da cor semântica.
   *
   * Não é camada de visibilidade e sim de pintura, por isso fica fora de
   * `camadas`: "escondi a estrutura" e "pintei por aço" são perguntas
   * diferentes, e misturá-las confundiria as duas.
   */
  pinturaPorAco: boolean
  /**
   * Cor de pintura da carroceria, quando o veículo é mostrado como veículo de
   * verdade (Table top) e não como peça didática. Carroceria e estrutura
   * (colunas, soleiras) levam a pintura; SRS e o resto, a cor semântica.
   */
  corCarroceria?: string
  /**
   * Âncoras cujo ocupante está sem cinto neste momento (P3.1).
   *
   * Não é estado de peça: o cinto continua existindo no veículo, só não está
   * sendo usado por aquele ocupante — por isso não passa por `estadoPecas`.
   */
  semCintoAncoras: readonly string[]
  /** Animação contínua do banco da âncora em foco durante a extração */
  animacaoBancoExtracao?: AnimacaoBancoExtracao | null
  /** Modo de exibição técnica: explodido em torno do monobloco */
  explodido?: boolean
  /** Último impacto geométrico registrado para inspeção semântica e depuração */
  ultimoHit?: HitVeiculo | null
  /** Ativa marcadores 3D e depuração de ponto/normal */
  depuracaoHit?: boolean
  /** Callback para registro de acerto espacial */
  aoRegistrarHit?: (hit: HitVeiculo | null) => void
}

export interface AnimacaoBancoExtracao {
  idAncora: string
  fracaoApoioCabeca: number
  fracaoAbaixarBanco: number
  /** rebatimento do encosto dos bancos de trás, para a frente; acontece antes de `fracaoAbaixarBanco` */
  fracaoRebaterBancosTraseiros?: number
  abaixarTodosBancos?: boolean
}

const ContextoMaquete = createContext<ValorMaquete | null>(null)

export function ProvedorMaquete({ valor, children }: { valor: ValorMaquete; children: ReactNode }) {
  return <ContextoMaquete.Provider value={valor}>{children}</ContextoMaquete.Provider>
}

/**
 * Se os rótulos flutuantes (`Rotulo3D`) devem aparecer. Fora de um provedor —
 * testes e componentes isolados — a resposta é não: rótulo é opt-in.
 */
export function useRotulosCenaVisiveis(): boolean {
  return useContext(ContextoMaquete)?.mostrarRotulos ?? false
}

export function useMaquete(): ValorMaquete {
  const valor = useContext(ContextoMaquete)
  if (!valor) throw new Error('useMaquete precisa estar dentro de <ProvedorMaquete>.')
  return valor
}

export interface AparenciaPeca {
  estado: EstadoPeca
  categoria: CategoriaDidatica
  selecionada: boolean
  /** a peça obstrui a rota em análise */
  emAlerta: boolean
  visivel: boolean
  cor: string
  /**
   * A cor vem da pintura do veículo (`corCarroceria`) e prevalece sobre o tom
   * que a peça pediria — senão cada chapa ficava no seu cinza de maquete.
   */
  corPrevalece: boolean
  opacidade: number
  emissivo: string
  intensidadeEmissiva: number
  transparente: boolean
  escreverProfundidade: boolean
}

const LIMITE_VISIVEL = 0.03

export function calcularAparencia(
  id: string,
  contexto: Pick<
    ValorMaquete,
    | 'camadas'
    | 'opacidadeCarroceria'
    | 'estados'
    | 'selecionada'
    | 'idsEmAlerta'
    | 'pinturaPorAco'
    | 'corCarroceria'
  >,
): AparenciaPeca {
  const categoria = categoriaDe(id)
  const estado = estadoDe(contexto.estados, id)
  const selecionada = contexto.selecionada === id
  // o alerta vale para o conjunto inteiro: a coluna B é desenhada em três
  // segmentos com ids próprios, e são eles que carregam a malha
  const emAlerta =
    contexto.idsEmAlerta.includes(id) || contexto.idsEmAlerta.includes(idRaiz(id))

  let opacidade =
    categoria === 'carroceria'
      ? contexto.opacidadeCarroceria
      : OPACIDADE_BASE[categoria]
  if (!contexto.camadas[categoria]) opacidade = 0

  if (pecaTranslucida(estado)) opacidade = Math.min(opacidade, 0.16)
  // destacar, alertar e selecionar nunca escondem a peça: trazem ela para frente
  if (estado === 'destacada') opacidade = Math.max(opacidade, 0.95)
  if (emAlerta) opacidade = Math.max(opacidade, 0.92)
  if (selecionada) opacidade = Math.max(opacidade, 0.9)

  const destacada = estado === 'destacada' || selecionada
  const visivel = pecaVisivel(estado) && opacidade > LIMITE_VISIVEL

  // peça sem classe declarada mantém a cor semântica: vidro, banco e airbag não
  // têm classe de aço, e pintá-los de cinza faria a camada afirmar o que não sabe
  const classe = contexto.pinturaPorAco ? classeAcoDe(id) : undefined
  // por fora, colunas e soleiras têm a cor da pintura do carro
  const pintado = !classe && (categoria === 'carroceria' || categoria === 'estrutura') && Boolean(contexto.corCarroceria)
  const corBase = classe ? corDaClasseAco(classe) : pintado ? contexto.corCarroceria! : corDe(id)

  return {
    estado,
    categoria,
    selecionada,
    emAlerta,
    visivel,
    cor: emAlerta ? CORES_SEMANTICAS.obstrucao : corBase,
    corPrevalece: pintado || emAlerta,
    opacidade,
    emissivo: emAlerta
      ? CORES_SEMANTICAS.obstrucao
      : corBase,
    // Com o contorno ativo via postprocessing, mantemos a cor e material originais sem lavar a peça de branco
    intensidadeEmissiva: emAlerta ? 0.6 : destacada ? 0.35 : selecionada ? 0.15 : 0.04,
    transparente: opacidade < 0.99,
    escreverProfundidade: opacidade > 0.9,
  }
}

export function usePeca(id: string) {
  const contexto = useMaquete()
  const aparencia = useMemo(
    () => calcularAparencia(id, contexto),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      id,
      contexto.camadas,
      contexto.opacidadeCarroceria,
      contexto.estados,
      contexto.selecionada,
      contexto.idsEmAlerta,
      contexto.pinturaPorAco,
      contexto.corCarroceria,
    ],
  )
  /* A peça só captura o clique onde selecioná-la significa alguma coisa.
     Fora do módulo de estrutura, a chapa da carroceria ficava na frente e
     engolia o toque com `stopPropagation` — era por isso que não dava para
     tocar na vítima dentro do habitáculo. */
  return {
    ...aparencia,
    selecionar: contexto.selecionar,
    mostrarRotulos: contexto.mostrarRotulos,
    capturaClique: PECA_SELECIONAVEL_EM.has(contexto.modo) || Boolean(contexto.depuracaoHit),
    aoRegistrarHit: contexto.aoRegistrarHit,
    depuracaoHit: contexto.depuracaoHit,
  }
}

/**
 * Modos em que tocar numa peça é um comando.
 *
 * Nos demais o clique pertence a outro objeto — a vítima na cinemática e na
 * extração, o calço na estabilização, o vidro no manejo.
 */
const PECA_SELECIONAVEL_EM = new Set<ModoVisualizacao>([
  'externo',
  'estrutura',
  'srs',
  'interior',
  'mecanica',
  'energia',
])
